import { DevTool } from '@hookform/devtools';
import { zodResolver } from '@hookform/resolvers/zod';
import type { PostTablesSettingsJumphosts200DataItem } from '@ipfabric/api-client/client/schemas/postTablesSettingsJumphosts200DataItem';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormIPSubnetField } from '@ipfabric/ui-components/FormIPSubnetField/FormIPSubnetField';
import { FormSelect } from '@ipfabric/ui-components/FormSelect/FormSelect';
import { FormSwitch } from '@ipfabric/ui-components/FormSwitch/FormSwitch';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import {
  type IPSubnetInputValueOrDraft,
  hasIPSubnetInputValueError,
  ipSubnetInputValueSchema,
} from '@ipfabric/ui-components/IPSubnetInput/IPSubnetInput.types';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import { isFeatureFlagEnabled } from '@ipfabric/ui-components/utils/featureFlags';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import {
  VALID_PASSWORD_REGEX,
  VALID_USERNAME_REGEX,
} from '@ipfabric/utils/constants';
import { addressSchema } from '@ipfabric/utils/schema/address';
import { isString } from 'lodash';
import { useWatch } from 'react-hook-form';
import { z } from 'zod';

interface JumphostDialogProps {
  onClose: () => void;
  type: 'add' | 'edit';
  onSubmit: (values: FormValues) => void;
  initialValues?: PostTablesSettingsJumphosts200DataItem;
}

export type JumphostLoginType = 'credentials' | 'ssh-key';
type LoginTypeOption = { value: JumphostLoginType; label: string };
const isJumphostLoginType = (value: unknown): value is JumphostLoginType =>
  value === 'credentials' || value === 'ssh-key';

export interface FormValues {
  enabled: boolean;
  label: string;
  address: string;
  port: string;
  subnets: IPSubnetInputValueOrDraft;
  exclude: IPSubnetInputValueOrDraft;
  loginType: JumphostLoginType;
  username: string;
  password: string;
}

export const JumphostDialog = ({
  onClose,
  type: formType,
  onSubmit,
  initialValues,
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: high complexity is acceptable for form
}: JumphostDialogProps) => {
  const { handleSubmit, formState, control } = useCustomForm<FormValues>({
    defaultValues: {
      enabled: initialValues?.enabled ?? true,
      label: initialValues?.label ?? '',
      address: initialValues?.address ?? '',
      port: initialValues?.port?.toString() ?? '22',
      subnets:
        initialValues?.subnets !== undefined
          ? { ips: initialValues?.subnets?.filter(isString) }
          : { ips: [] },
      exclude:
        initialValues?.exclude !== undefined
          ? { ips: initialValues?.exclude?.filter(isString) }
          : { ips: [] },
      loginType: isJumphostLoginType(initialValues?.loginType)
        ? initialValues?.loginType
        : 'credentials',
      username: '',
      password: '',
    },
    resolver: zodResolver(getValidationSchema(formType)),
  });

  const loginType = useWatch({ control, name: 'loginType' });
  const enabled = useWatch({ control, name: 'enabled' });

  return (
    <SidePanel
      title={formType === 'add' ? 'Create new jumphost' : 'Edit jumphost'}
      closeOnBackdropClick={false}
      onClose={onClose}
      footer={({ closeModal }) => (
        <div className='flex gap-xs'>
          <Button
            className='flex-1'
            variant='primary'
            type='submit'
            icon='fa-floppy-disk'
            loading={formState.isSubmitting}
            onClick={handleSubmit(onSubmit)}
            disabled={!formState.isDirty}
          >
            {formType === 'add' ? 'Add jumphost' : 'Update jumphost'}
          </Button>
          <Button className='flex-1' variant='outline' onClick={closeModal}>
            Close
          </Button>
        </div>
      )}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        {isFeatureFlagEnabled('ENABLE_HOOK_FORM_DEV_TOOLS') && (
          <DevTool control={control} placement='top-left' />
        )}
        <Card className='flex flex-col gap p-sm'>
          {formType === 'edit' && (
            <FormSwitch
              control={control}
              name='enabled'
              description='Enabled'
              disabled={formState.isSubmitting}
            />
          )}
          <FormTextField
            control={control}
            name='label'
            label='Label'
            type='text'
            disabled={!enabled || formState.isSubmitting}
          />
          <FormTextField
            control={control}
            name='address'
            label='Jumphost address'
            type='text'
            disabled={!enabled || formState.isSubmitting}
          />
          <FormTextField
            control={control}
            name='port'
            label='Port'
            type='text'
            disabled={!enabled || formState.isSubmitting}
          />
          <FormIPSubnetField
            control={control}
            name='subnets'
            label='Included subnets'
            type='cidr'
            disabled={!enabled || formState.isSubmitting}
            placeholder='Insert one or more subnets'
          />
          <FormIPSubnetField
            control={control}
            name='exclude'
            label='Excluded subnets'
            type='cidr'
            disabled={!enabled || formState.isSubmitting}
            placeholder='Insert one or more subnets'
          />
          <FormSelect.Mandatory
            control={control}
            name='loginType'
            label='Login method'
            options={
              [
                { value: 'credentials', label: 'Credentials' },
                { value: 'ssh-key', label: 'SSH key' },
              ] satisfies LoginTypeOption[]
            }
            disabled={!enabled || formState.isSubmitting}
          />
          <FormTextField
            control={control}
            name='username'
            label='Username'
            type='text'
            disabled={!enabled || formState.isSubmitting}
          />
          {loginType === 'credentials' && (
            <FormTextField
              control={control}
              name='password'
              label='Password'
              type='password'
              disabled={!enabled || formState.isSubmitting}
            />
          )}
        </Card>
      </form>
    </SidePanel>
  );
};

const getValidationSchema = (formType: 'add' | 'edit') => {
  return (
    z
      .object({
        id: z.string().optional(),
        enabled: z.boolean(),
        label: z.string().min(1, {
          message: 'Label is required',
        }),
        address: addressSchema,
        port: z.string().optional(),
        subnets: ipSubnetInputValueSchema,
        exclude: ipSubnetInputValueSchema.optional(),
        loginType: z.union([z.literal('credentials'), z.literal('ssh-key')]),
        username: z.string().optional(),
        password: z
          .string()
          .regex(
            VALID_PASSWORD_REGEX,
            `Password must match the pattern: ${VALID_PASSWORD_REGEX}`,
          )
          .optional(),
      })
      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: high complexity is acceptable for form validation
      .superRefine((data, ctx) => {
        const numberRegex = /^\d+$/;

        if (data.port && !numberRegex.test(data.port)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Port must be a number',
            path: ['port'],
          });
        }

        if (data.port && (Number(data.port) < 1 || Number(data.port) > 65535)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Port must be greater than 0 and less than 65536',
            path: ['port'],
          });
        }

        if (!data.username && formType === 'add') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Username is required',
            path: ['username'],
          });
        }

        if (data.username && !data.username.match(VALID_USERNAME_REGEX)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Username must match the pattern: ${VALID_USERNAME_REGEX}`,
            path: ['username'],
          });
        }

        if (data.loginType === 'credentials' && formType === 'add') {
          if (!data.password) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Password is required',
              path: ['password'],
            });
          }
        }

        if (hasIPSubnetInputValueError(data.subnets)) {
          // Display the error message from the draft value
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: data.subnets.message,
            path: ['subnets'],
          });
        } else if (data.subnets.ips.length === 0) {
          // Validate that at least one subnet is provided
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one subnet is required',
            path: ['subnets'],
          });
        }

        if (
          data.exclude !== undefined &&
          hasIPSubnetInputValueError(data.exclude)
        ) {
          // Display the error message from the draft value
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: data.exclude.message,
            path: ['exclude'],
          });
        }
      })
  );
};
